//src/context/AuthContext.jsx

/**
 * Authentication Context - Phase 7: Cart Migration
 * 
 * PHASE 7 CART MIGRATION:
 * - On login/register: migrate guest cart to backend (REPLACE strategy)
 * - Call cartService.replaceCart() with guest cart items
 * - Clear localStorage cart after migration
 * - CartContext will auto-fetch backend cart
 */

'use client';

import { createContext, useContext, useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { getStorageItem, setStorageItem, removeStorageItem } from '@/utils/storage';
import * as authService from '@/services/authService';
import * as cartService from '@/services/cartService';
import { isTokenExpired } from '@/utils/jwt';
import { logger } from '@/utils/logger';

/**
 * Authentication Context
 * 
 * Manages authentication state across the app
 * - Persists tokens in localStorage
 * - Provides login/logout functions
 * - Exposes authentication status
 * 
 * HYBRID MODE:
 * - Public browsing works without auth
 * - Auth required only for checkout/orders
 */

const AuthContext = createContext(undefined);
const defaultAuthContext = {
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  hasActiveSession: false,
  loading: false,
  login: async () => {
    throw new Error('AuthProvider is not mounted');
  },
  register: async () => {
    throw new Error('AuthProvider is not mounted');
  },
  logout: async () => {},
  loginWithGoogle: async () => {
    throw new Error('AuthProvider is not mounted');
  },
  refreshSession: async () => null,
};

const AUTH_BOOTSTRAP_ROUTES = [
  '/cart',
  '/checkout',
  '/payment',
  '/profile',
  '/orders',
  '/order-success',
  '/order-confirmation',
];

const shouldBootstrapAuth = (pathname) =>
  AUTH_BOOTSTRAP_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );

const withTimeout = async (promise, ms, fallback = null) => {
  let timer;
  try {
    return await Promise.race([
      promise,
      new Promise((resolve) => {
        timer = setTimeout(() => resolve(fallback), ms);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
};

export function AuthProvider({ children }) {
  const pathname = usePathname();
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [hasActiveSession, setHasActiveSession] = useState(false);
  const [loading, setLoading] = useState(() => shouldBootstrapAuth(pathname));

  const persistSession = ({
    user: nextUser,
    accessToken: nextAccessToken = null,
    refreshToken: nextRefreshToken = null,
  }) => {
    setUser(nextUser || null);
    setAccessToken(nextAccessToken || null);
    setRefreshToken(nextRefreshToken || null);

    if (nextUser) {
      setStorageItem('user', nextUser);
    } else {
      removeStorageItem('user');
    }
    // Cookie-only auth mode: never persist tokens in localStorage/sessionStorage.
    removeStorageItem('accessToken');
    removeStorageItem('refreshToken');
  };

  // Bootstrap auth state from HttpOnly cookies (via refresh endpoint).
  useEffect(() => {
    const loadAuthState = async () => {
      const storedUser = getStorageItem('user');
      // Cleanup any legacy token persistence from previous builds.
      removeStorageItem('accessToken');
      removeStorageItem('refreshToken');

      if (!shouldBootstrapAuth(pathname)) {
        if (storedUser) {
          persistSession({
            user: storedUser,
            accessToken: null,
            refreshToken: null,
          });
        }
        setHasActiveSession(false);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        // Cookie-first bootstrap: if the HttpOnly access cookie is still valid,
        // keep the session alive without depending on refresh rotation.
        const cookieUser = await withTimeout(
          authService.getCurrentUser(null),
          4000,
          null,
        );
        if (cookieUser) {
          persistSession({
            user: cookieUser,
            accessToken: null,
            refreshToken: null,
          });
          setHasActiveSession(true);
          setLoading(false);
          return;
        }

        const refreshed = await withTimeout(
          authService.refreshAccessToken(),
          4000,
          null,
        );
        const resolvedUser = refreshed?.user
          || (refreshed?.accessToken
            ? await withTimeout(
                authService.getCurrentUser(refreshed.accessToken),
                4000,
                null,
              )
            : null);

        if (resolvedUser) {
          persistSession({
            user: resolvedUser,
            accessToken: refreshed?.accessToken || null,
            refreshToken: refreshed?.refreshToken || null,
          });
          setHasActiveSession(true);
        } else if (storedUser) {
          // Preserve the last known user when the live auth bootstrap is flaky.
          persistSession({
            user: storedUser,
            accessToken: null,
            refreshToken: null,
          });
          setHasActiveSession(false);
        } else {
          removeStorageItem('user');
          setUser(null);
          setAccessToken(null);
          setRefreshToken(null);
          setHasActiveSession(false);
        }
      } catch (error) {
        if (storedUser) {
          persistSession({
            user: storedUser,
            accessToken: null,
            refreshToken: null,
          });
          setHasActiveSession(false);
        } else {
          removeStorageItem('user');
          setUser(null);
          setAccessToken(null);
          setRefreshToken(null);
          setHasActiveSession(false);
        }
      }

      setLoading(false);
    };

    loadAuthState();
  }, [pathname]);

  /**
   * Login user with email and password
   * Phase 7: Migrate guest cart to backend after login
   */
  const login = async (email, password) => {
    try {
      const sessionData = await authService.login(email, password);
      const resolvedUser =
        sessionData?.user || (await authService.getCurrentUser(sessionData?.accessToken || null));
      if (!resolvedUser) {
        throw new Error('Login succeeded but user profile could not be loaded');
      }

      persistSession({
        user: resolvedUser,
        accessToken: sessionData?.accessToken || null,
        refreshToken: sessionData?.refreshToken || null,
      });
      setHasActiveSession(true);

      // Phase 7: Migrate guest cart to backend (REPLACE strategy)
      if (sessionData?.accessToken) {
        await migrateGuestCartToBackend(sessionData.accessToken);
      }

      return resolvedUser;
    } catch (error) {
      logger.error('[AUTH_CONTEXT] Login failed:', error);
      throw error;
    }
  };

  /**
   * Register new user
   * Phase 7: Migrate guest cart to backend after registration
   */
  const register = async (userData) => {
    try {
      const sessionData = await authService.register(userData);
      const resolvedUser =
        sessionData?.user || (await authService.getCurrentUser(sessionData?.accessToken || null));
      if (!resolvedUser) {
        throw new Error('Registration succeeded but user profile could not be loaded');
      }

      persistSession({
        user: resolvedUser,
        accessToken: sessionData?.accessToken || null,
        refreshToken: sessionData?.refreshToken || null,
      });
      setHasActiveSession(true);

      // Phase 7: Migrate guest cart to backend (REPLACE strategy)
      if (sessionData?.accessToken) {
        await migrateGuestCartToBackend(sessionData.accessToken);
      }

      return resolvedUser;
    } catch (error) {
      logger.error('[AUTH_CONTEXT] Registration failed:', error);
      throw error;
    }
  };

  /**
   * Helper: Migrate guest cart to backend
   * Phase 7: REPLACE strategy - overwrites backend cart with guest cart
   */
  const migrateGuestCartToBackend = async (token) => {
    try {
      // Read guest cart from localStorage
      const guestCartRaw = getStorageItem('cart_items');
      
      if (!guestCartRaw) {
        return;
      }

      const guestCart = Array.isArray(guestCartRaw) ? guestCartRaw : [];
      
      if (!guestCart || guestCart.length === 0) {
        removeStorageItem('cart_items');
        return;
      }

      // Call cartService.replaceCart (REPLACE strategy)
      await cartService.replaceCart(token, guestCart);

      // Clear localStorage cart after successful migration
      removeStorageItem('cart_items');

      // Note: CartContext will auto-fetch backend cart via useEffect
    } catch (error) {
      logger.error('[AUTH_CONTEXT] Cart migration failed:', error);
      // Don't block login on cart migration failure
      // User can manually add items to cart after login
    }
  };

  /**
   * Logout user
   * Phase 7: Cart reverts to guest mode automatically (CartContext handles this)
   */
  /**
   * Login with Google
   * Phase 7: Migrate guest cart to backend after login
   */
  const loginWithGoogle = async (idToken) => {
    try {
      const sessionData = await authService.googleLogin(idToken);
      const resolvedUser =
        sessionData?.user || (await authService.getCurrentUser(sessionData?.accessToken || null));
      if (!resolvedUser) {
        throw new Error('Google login succeeded but user profile could not be loaded');
      }

      persistSession({
        user: resolvedUser,
        accessToken: sessionData?.accessToken || null,
        refreshToken: sessionData?.refreshToken || null,
      });
      setHasActiveSession(true);

      // Phase 7: Migrate guest cart to backend (REPLACE strategy)
      if (sessionData?.accessToken) {
        await migrateGuestCartToBackend(sessionData.accessToken);
      }

      return resolvedUser;
    } catch (error) {
      logger.error('[AUTH_CONTEXT] Google Login failed:', error);
      throw error;
    }
  };

  /**
   * Logout user
   * Phase 7: Cart reverts to guest mode automatically (CartContext handles this)
   */
  const logout = async () => {
    // 1. Clear State & LocalStorage FIRST (Stability Fix)
    const tokenToRevoke = accessToken;
    const refreshToRevoke = refreshToken;



    // Clear state
    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
    setHasActiveSession(false);

    // Clear localStorage
    removeStorageItem('accessToken');
    removeStorageItem('refreshToken');
    removeStorageItem('user');
    try {
      // 2. Call backend logout (best effort) AFTER clearing local state
      // Cookie-first auth means tokens may be null in memory, but the browser
      // can still carry HttpOnly cookies to the logout endpoint.
      await authService.logout(tokenToRevoke, refreshToRevoke);
    } catch (error) {
      logger.warn('[AUTH_CONTEXT] Logout API call failed (ignored)');
    }
  };

  /**
   * Check if user is authenticated
   */
  const isAuthenticated = Boolean(user);

  /**
   * Get user display name
   */
  const getUserDisplayName = () => {
    if (!user) return null;
    return user.firstName || user.email || user.phone || 'User';
  };

  const value = {
    user,
    accessToken,
    refreshToken,
    hasActiveSession,
    isAuthenticated,
    loading,
    login,
    register,
        loginWithGoogle,
    logout,
    getUserDisplayName,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to use auth context
 */
export function useAuth() {
  const context = useContext(AuthContext);
  return context === undefined ? defaultAuthContext : context;
}
